import { useState } from 'react'
import Layout from './components/Layout'
import Step1BasicInfo from './components/FormSteps/Step1BasicInfo'
import Step2Details from './components/FormSteps/Step2Details'
import Step3Memo from './components/FormSteps/Step3Memo'
import Step4Consent from './components/FormSteps/Step4Consent'
import CompletionScreen from './components/CompletionScreen'
import StepIndicator from './components/StepIndicator'
import type { PatientRecord, PatientBasicInfo, PatientDetails, PatientMemo, PatientConsent } from 'shared'
import { savePatientRecord } from './services/db'

// ブラウザ環境でUUIDを生成
const generateUUID = (): string => {
  return crypto.randomUUID()
}

function App() {
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState<Partial<PatientRecord>>({})
  const [, setIsSubmitting] = useState(false)

  const totalSteps = 4

  const handleNextStep = (stepData: Partial<PatientRecord>) => {
    setFormData((prev) => ({ ...prev, ...stepData }))
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps))
  }

  const handlePrevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1))
  }

  const handleComplete = async (finalData: Partial<PatientRecord>) => {
    setIsSubmitting(true)
    try {
      const completeData = { ...formData, ...finalData }

      // PatientRecordに変換してIndexedDBに保存
      const now = new Date()
      const patientRecord: PatientRecord = {
        id: generateUUID(),
        basicInfo: completeData.basicInfo as PatientBasicInfo,
        details: completeData.details as PatientDetails,
        memo: completeData.memo as PatientMemo,
        consent: completeData.consent as PatientConsent,
        firstVisitDate: now,
        createdAt: now,
        updatedAt: now,
      }

      await savePatientRecord(patientRecord)

      // 完了画面を表示
      setCurrentStep(5)
    } catch (error) {
      alert('カルテデータの保存に失敗しました')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReset = () => {
    setFormData({})
    setCurrentStep(1)
  }

  return (
    <Layout
      title="初回来店カルテ"
      rightContent={
        currentStep <= totalSteps ? (
          <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />
        ) : null
      }
    >
      <div className="px-6 py-6">
        {currentStep === 1 && (
          <Step1BasicInfo
            initialData={formData}
            onNext={handleNextStep}
          />
        )}

        {currentStep === 2 && (
          <Step2Details
            initialData={formData}
            onNext={handleNextStep}
            onBack={handlePrevStep}
          />
        )}

        {currentStep === 3 && (
          <Step3Memo
            initialData={formData}
            onNext={handleNextStep}
            onBack={handlePrevStep}
          />
        )}

        {currentStep === 4 && (
          <Step4Consent
            formData={formData}
            onComplete={handleComplete}
            onBack={handlePrevStep}
          />
        )}

        {currentStep === 5 && (
          <CompletionScreen onReset={handleReset} />
        )}
      </div>
    </Layout>
  )
}

export default App
